import React, { useContext } from "react";
import { Accordion, AccordionBody, AccordionHeader, SubSection, useBreakpoints } from "best-common-react";
import styled, { ThemeContext } from "styled-components";
import { Card, Item, Label, Row, Value } from "./ReadOnlyProfileStyles";
import { useProspectProfile } from "../../../contexts/ProsepctProfileContext";
import { useTranslation } from "react-i18next";
import { useMetadata } from "../../../contexts/MetadataContext";
import useLanguage from "../../../hooks/useLanguage";
import { dateFormats, formatLocalDate } from "../../../util/ProfileUtils";
import { CountryIds } from "../../../constants/CountryConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { ProspectTravelDateDTO } from "../../../types/ProspectProfile";
import { format, parse } from "date-fns";

const CardInfo = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-basis: 50%;
  flex-direction: column;
`;

const Placeholder = styled.div`
  height: 42px;
`;

type TravelDateReadOnlyCardProps = {
  travelDate: ProspectTravelDateDTO;
};

const TravelDateReadOnlyCard: React.FC<TravelDateReadOnlyCardProps> = ({ travelDate }) => {
  const { t } = useTranslation(["translation", "profile"]);
  const { countries } = useMetadata();

  const country = countries.find((country) => country.countryId === travelDate.countryId);
  const countryName = !!country?.esDescription?.length ? country.esDescription : country.description;

  const startDate = format(parse(travelDate.startDate, dateFormats.YYYY_MM_DD, new Date()), dateFormats.MM_DD_YYYY);
  const endDate = format(parse(travelDate.endDate, dateFormats.YYYY_MM_DD, new Date()), dateFormats.MM_DD_YYYY);

  return (
    <Card>
      <CardInfo>
        <Column>
          <div className="mb-2">
            <Label>{t("translation:startDate")}</Label>
            <Value>{startDate}</Value>
          </div>
          <div>
            <Label>{t("profile:country")}</Label>
            <Value>{countryName}</Value>
          </div>
          <Placeholder />
        </Column>
        <Column>
          <div className="mb-2">
            <Label>{t("translation:endDate")}</Label>
            <Value>{endDate}</Value>
          </div>
          <div>
            <Label>{t("profile:stateTerritory")}</Label>
            <Value>{travelDate.state}</Value>
          </div>
          <div>
            <Label>{t("profile:city")}</Label>
            <Value>{travelDate.city}</Value>
          </div>
        </Column>
        <div className="mt-2" style={{ flexBasis: "100%" }}>
          <Label>{t("profile:purpose")}</Label>
          <Value>{travelDate.purpose}</Value>
        </div>
      </CardInfo>
    </Card>
  );
};

const BioInfoReadyOnly: React.FC = () => {
  const { t } = useTranslation(["common", "profile"]);
  const Theme = useContext(ThemeContext);
  const { isMobile } = useBreakpoints();
  const { profile } = useProspectProfile();
  const { isEs } = useLanguage();
  const { countries } = useMetadata();
  const { getYesNoRadioValues } = useDropdowns();

  const registrationCountry = countries?.find((c) => c.countryId === profile.registrationCountryId);
  const birthCountry = countries?.find((c) => c.countryId === profile.birthCountryId);

  return (
    <Accordion
      accordionKey="basball-info"
      styles={{ ...Theme.accordion, body: { ...Theme.accordion.body, background: Theme.colors.cloud, border: "none" } }}
    >
      <AccordionHeader>{t("profile:bioInformationSection")}</AccordionHeader>
      <AccordionBody>
        <Row>
          <Item isMobile={isMobile}>
            <Label>{t("profile:registrationCountry")}</Label>
            <Value>
              {isEs && !!registrationCountry?.esDescription?.length
                ? registrationCountry?.esDescription
                : registrationCountry?.description}
            </Value>
          </Item>
          <Item isMobile={isMobile}>
            <Label>{t("profile:birthDate")}</Label>
            <Value>{formatLocalDate(profile.dateOfBirth)}</Value>
          </Item>
        </Row>
        <Row>
          <Item isMobile={isMobile}>
            <div className="mb-1">
              <Label>{t("profile:hasTwin")}</Label>
              <Value>{profile.hasTwin ? t("common:yes") : t("common:no")}</Value>
            </div>

            {profile.hasTwin && (
              <div>
                <Label>{t("profile:twinCount")}</Label>
                <Value>{profile.twinCount}</Value>
              </div>
            )}
          </Item>
          <Item isMobile={isMobile}>
            <div className="mb-1">
              <Label>{t("profile:birthCountry")}</Label>
              <Value>
                {isEs && !!birthCountry?.esDescription?.length
                  ? birthCountry?.esDescription
                  : birthCountry?.description}
              </Value>
            </div>

            <div className="mb-1">
              <Label>{t("profile:birthStateProvince")}</Label>
              <Value>{profile.birthStateProvince}</Value>
            </div>

            <div>
              <Label>{t("profile:birthCity")}</Label>
              <Value>{profile.birthCity}</Value>
            </div>
          </Item>
        </Row>

        {profile?.birthCountryId === CountryIds.CUBA && (
          <Row>
            <div>
              <Label>{t("profile:livesInUsCanPr")}</Label>
              <Value>{getYesNoRadioValues(profile?.livesInUsCanPr)?.label}</Value>
            </div>
          </Row>
        )}

        <Row>
          <div className="mb-1">
            <Label>{t("profile:traveledToUsCanPr")}</Label>
            <Value>{profile.traveledToUsCanPrFlag ? t("common:yes") : t("common:no")}</Value>
          </div>

          {profile.traveledToUsCanPrFlag && (
            <div>
              <div className="mb-1">
                <Label>{t("profile:usCanPrCount")}</Label>
                <Value>{profile.traveledToUsCanPrCount}</Value>
              </div>

              <div className="mb-1">
                <Label>{t("profile:longestStayUsCanPr")}</Label>
                <Value>{profile.longestStayUsCanPr}</Value>
              </div>

              <div>
                <Label>{t("profile:mostRecentUsCanPr")}</Label>
                <Value>{formatLocalDate(profile.recentStayUsPrCan)}</Value>
              </div>
            </div>
          )}
        </Row>

        {profile?.birthCountryId === CountryIds.CUBA && (
          <SubSection header={t("profile:travelDates")}>
            {profile?.travelDates.map((travelDate) => (
              <TravelDateReadOnlyCard key={`travel-date-card-${travelDate.id}`} travelDate={travelDate} />
            ))}
          </SubSection>
        )}
      </AccordionBody>
    </Accordion>
  );
};

export default BioInfoReadyOnly;
